// Global audio player store. A single <audio> element lives in <GlobalAudioHost>
// and is the source of truth for ALL playback in the app. Per-page players bind
// to this store so audio survives navigation, and a fixed mini bar exposes
// play/pause/scrub controls from anywhere.

import { useSyncExternalStore } from "react";

export interface EpisodeMeta {
  /** Stable id — episodeId when available, else the audio src URL. */
  key: string;
  episodeId?: string | null;
  src: string;
  title: string;
  podcastName?: string;
  artworkUrl?: string;
  /** Slug used to deep-link from the mini bar back to the episode page. */
  episodeSlug?: string | null;
  /** Optional cued moment from a search hit. */
  startTime?: number | null;
}

export interface PlayerState {
  current: EpisodeMeta | null;
  playing: boolean;
  currentTime: number;
  duration: number;
  muted: boolean;
  /** True while the audio element is loading metadata or waiting for buffer. */
  buffering: boolean;
  /** Bumped whenever an explicit seek-and-play is requested (e.g. "jump to moment"). */
  seekSignal: number;
  /** Last requested seek target in seconds — used by GlobalAudioHost. */
  seekTarget: number | null;
}

type Listener = () => void;

const PERSIST_KEY = "atp:player-state-v1";

interface PersistedSnapshot {
  current: EpisodeMeta | null;
  currentTime: number;
  duration: number;
  playing: boolean;
}

function loadPersisted(): PersistedSnapshot | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(PERSIST_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedSnapshot>;
    if (!parsed?.current?.src) return null;
    return {
      current: parsed.current as EpisodeMeta,
      currentTime: Number(parsed.currentTime) || 0,
      duration: Number(parsed.duration) || 0,
      playing: Boolean(parsed.playing),
    };
  } catch {
    return null;
  }
}

const hydrated = loadPersisted();

let state: PlayerState = {
  current: hydrated?.current ?? null,
  // Browsers block autoplay without a user gesture; rehydrate paused but
  // keep the mini bar visible so the user can tap play to resume in place.
  playing: false,
  currentTime: hydrated?.currentTime ?? 0,
  duration: hydrated?.duration ?? 0,
  muted: false,
  buffering: false,
  // moves the <audio> element back to where the user left off as soon as
  // metadata loads.
  seekSignal: hydrated && hydrated.currentTime > 0 ? 1 : 0,
  seekTarget: hydrated && hydrated.currentTime > 0 ? hydrated.currentTime : null,
};

const listeners = new Set<Listener>();

function emit() {
  for (const l of listeners) l();
}

let persistTimer: number | null = null;
function schedulePersist() {
  if (typeof window === "undefined") return;
  if (persistTimer != null) return;
  persistTimer = window.setTimeout(() => {
    persistTimer = null;
    try {
      if (!state.current) {
        localStorage.removeItem(PERSIST_KEY);
        return;
      }
      const snapshot: PersistedSnapshot = {
        current: state.current,
        currentTime: state.currentTime,
        duration: state.duration,
        playing: state.playing,
      };
      localStorage.setItem(PERSIST_KEY, JSON.stringify(snapshot));
    } catch { /* ignore quota / private-mode errors */ }
  }, 500);
}

function setState(patch: Partial<PlayerState>) {
  state = { ...state, ...patch };
  emit();
  schedulePersist();
}

export const playerStore = {
  getState: () => state,
  subscribe(listener: Listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  /** Load a new episode and start playing on the next user gesture / autoplay. */
  playEpisode(meta: EpisodeMeta) {
    const sameTrack = state.current?.key === meta.key;
    if (sameTrack) {
      // Same episode — just resume playback.
      setState({ playing: true });
      return;
    }
    setState({
      current: meta,
      playing: true,
      currentTime: 0,
      duration: 0,
      buffering: true,
      seekTarget: meta.startTime ?? null,
      seekSignal: meta.startTime != null ? state.seekSignal + 1 : state.seekSignal,
    });
  },
  toggle() {
    if (!state.current) return;
    setState({ playing: !state.playing });
  },
  pause() {
    setState({ playing: false });
  },
  resume() {
    if (state.current) setState({ playing: true });
  },
  seek(seconds: number) {
    setState({ seekTarget: seconds, seekSignal: state.seekSignal + 1 });
  },
  setMuted(muted: boolean) {
    setState({ muted });
  },
  /** Called by GlobalAudioHost to mirror <audio> element state. */
  _reportTime(currentTime: number, duration: number) {
    state = { ...state, currentTime, duration };
    emit();
    schedulePersist();
  },
  _reportPlaying(playing: boolean) {
    if (state.playing !== playing) setState({ playing });
  },
  _reportBuffering(buffering: boolean) {
    if (state.buffering !== buffering) setState({ buffering });
  },
  _consumeSeek() {
    if (state.seekTarget != null) setState({ seekTarget: null });
  },
  stop() {
    setState({
      current: null,
      playing: false,
      currentTime: 0,
      duration: 0,
      seekTarget: null,
    });
  },
};

export function usePlayer<T>(selector: (s: PlayerState) => T): T {
  return useSyncExternalStore(
    playerStore.subscribe,
    () => selector(state),
    () => selector(state),
  );
}

export function buildEpisodeKey(episodeId?: string | null, src?: string | null): string {
  return episodeId || src || "";
}
